import { useState, useEffect, useRef } from 'react';
import { api, API_BASE } from '../../utils/api';
import { Button, useToast, Card } from '../shared/UI';

function MediaItem({ item, onToggle, onDelete, onMove, index, total }) {
  const typeIcon = item.media_type === 'video' ? '▷' : '🖼';
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px',
      background: item.is_active ? '#fff' : 'var(--gray-50)', borderRadius: 12,
      border: `1.5px solid ${item.is_active ? 'var(--gray-100)' : 'var(--gray-200)'}`,
      opacity: item.is_active ? 1 : 0.6, animation: 'fadeIn 0.3s ease',
    }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <button onClick={() => onMove(item.id, -1)} disabled={index === 0} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray-400)', fontSize: 12, lineHeight: 1, padding: 2 }}>▲</button>
        <button onClick={() => onMove(item.id, 1)} disabled={index === total - 1} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray-400)', fontSize: 12, lineHeight: 1, padding: 2 }}>▼</button>
      </div>
      <div style={{ width: 36, height: 36, background: item.media_type === 'video' ? 'var(--navy)' : 'var(--cobalt)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>
        {typeIcon}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--navy)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.original_name || item.file_path.split('/').pop()}</div>
        <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 2 }}>
          {item.media_type} · {item.media_type === 'image' ? `${item.duration_sec}s display` : 'auto-duration'} · Order #{item.sort_order}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <Button onClick={() => onToggle(item)} variant={item.is_active ? 'ghost' : 'success'} size="sm">
          {item.is_active ? 'Disable' : 'Enable'}
        </Button>
        <Button onClick={() => onDelete(item)} variant="danger" size="sm">✕</Button>
      </div>
    </div>
  );
}

export function MediaManager() {
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadFileName, setUploadFileName] = useState('');
  const [duration, setDuration] = useState(10);
  const fileRef = useRef();
  const { show, ToastContainer } = useToast();

  const load = () => api.get('/api/media/').then(setItems);
  useEffect(() => { load(); }, []);

  const upload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    setUploadProgress(0);
    setUploadFileName(file.name);

    const fd = new FormData();
    fd.append('file', file);
    fd.append('duration_sec', duration);

    // Use XMLHttpRequest for real-time upload progress tracking
    const xhr = new XMLHttpRequest();
    const BASE = process.env.REACT_APP_API_URL || '';

    xhr.open('POST', `${BASE}/api/media/upload`, true);
    xhr.withCredentials = true;

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        const pct = Math.round((event.loaded / event.total) * 100);
        setUploadProgress(pct);
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        show('Media uploaded successfully! 🎉');
        load();
      } else {
        let msg = 'Upload failed';
        try { msg = JSON.parse(xhr.responseText)?.error || msg; } catch (_) {}
        show(msg, 'error');
      }
      setUploading(false);
      setUploadProgress(0);
      setUploadFileName('');
      if (fileRef.current) fileRef.current.value = '';
    };

    xhr.onerror = () => {
      show('Upload failed — network error', 'error');
      setUploading(false);
      setUploadProgress(0);
      setUploadFileName('');
      if (fileRef.current) fileRef.current.value = '';
    };

    xhr.send(fd);
  };

  const toggle = async (item) => {
    await api.put(`/api/media/${item.id}`, { is_active: !item.is_active });
    load();
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete "${item.original_name || item.file_path.split('/').pop()}"?`)) return;
    await api.delete(`/api/media/${item.id}`);
    show('Deleted'); load();
  };

  const move = async (id, dir) => {
    const sorted = [...items].sort((a, b) => a.sort_order - b.sort_order);
    const idx = sorted.findIndex(i => i.id === id);
    const swapIdx = idx + dir;
    if (swapIdx < 0 || swapIdx >= sorted.length) return;
    const reordered = sorted.map((item, i) => {
      if (i === idx) return { ...item, sort_order: sorted[swapIdx].sort_order };
      if (i === swapIdx) return { ...item, sort_order: sorted[idx].sort_order };
      return item;
    });
    await api.post('/api/media/reorder', reordered.map(i => ({ id: i.id, sort_order: i.sort_order })));
    load();
  };

  const activeItems = items.filter(i => i.is_active).sort((a, b) => a.sort_order - b.sort_order);
  const inactiveItems = items.filter(i => !i.is_active).sort((a, b) => a.sort_order - b.sort_order);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 28, maxWidth: 780 }}>
      <ToastContainer />
      <div>
        <h1 style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 28, color: 'var(--navy)' }}>Media / Ads Manager</h1>
        <p style={{ color: 'var(--gray-500)', marginTop: 4, fontSize: 14 }}>Upload and arrange videos and images for the display screen playlist</p>
      </div>

      {/* Upload zone */}
      <Card>
        <div style={{ padding: 24 }}>
          <h2 style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 16, color: 'var(--navy)', marginBottom: 16 }}>Upload Media</h2>
          <div
            onClick={() => !uploading && fileRef.current?.click()}
            style={{
              border: `2px dashed ${uploading ? 'var(--cobalt)' : 'var(--cobalt-bright)'}`, borderRadius: 14, padding: '32px 24px',
              textAlign: 'center', cursor: uploading ? 'not-allowed' : 'pointer',
              background: uploading ? 'rgba(21,101,192,0.06)' : 'var(--ice)',
              transition: 'var(--transition)',
            }}
            onMouseEnter={e => { if (!uploading) e.currentTarget.style.background = 'var(--ice-dim)'; }}
            onMouseLeave={e => { if (!uploading) e.currentTarget.style.background = 'var(--ice)'; }}>
            <div style={{ fontSize: 32, marginBottom: 10 }}>{uploading ? '⏳' : '📁'}</div>
            <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, color: 'var(--cobalt)', marginBottom: 6 }}>
              {uploading ? `Uploading: ${uploadFileName}` : 'Click to upload a file'}
            </div>
            <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>
              MP4, WebM, MOV, JPG, PNG, GIF, WebP · No file size limit
            </div>
            <input ref={fileRef} type="file" accept="video/*,image/*" style={{ display: 'none' }} onChange={upload} />
          </div>

          {/* Upload progress bar */}
          {uploading && (
            <div style={{ marginTop: 18 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--navy)', fontFamily: 'var(--font-display)' }}>
                  Uploading...
                </span>
                <span style={{ fontSize: 13, fontWeight: 800, color: 'var(--cobalt)', fontFamily: 'var(--font-mono)' }}>
                  {uploadProgress}%
                </span>
              </div>
              {/* Track */}
              <div style={{ height: 10, background: 'var(--gray-100)', borderRadius: 99, overflow: 'hidden' }}>
                {/* Fill */}
                <div style={{
                  height: '100%',
                  width: `${uploadProgress}%`,
                  background: 'linear-gradient(90deg, var(--cobalt), var(--cobalt-bright))',
                  borderRadius: 99,
                  transition: 'width 0.25s ease',
                  boxShadow: '0 0 8px rgba(66,165,245,0.5)',
                }} />
              </div>
              {uploadProgress === 100 && (
                <div style={{ fontSize: 12, color: 'var(--green-dark)', marginTop: 6, fontWeight: 600 }}>
                  ✓ Transfer complete — processing on server...
                </div>
              )}
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 16 }}>
            <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--gray-700)' }}>Image display duration (seconds):</label>
            <input type="number" min={3} max={120} value={duration} onChange={e => setDuration(parseInt(e.target.value))}
              style={{ width: 70, padding: '6px 10px', borderRadius: 6, border: '1.5px solid var(--gray-100)', fontSize: 14, fontFamily: 'var(--font-mono)' }} />
          </div>
        </div>
      </Card>

      {/* Active playlist */}
      <div>
        <h2 style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 16, color: 'var(--navy)', marginBottom: 14 }}>
          Active Playlist <span style={{ fontWeight: 400, color: 'var(--gray-500)', fontSize: 13 }}>({activeItems.length} items · loops continuously)</span>
        </h2>
        {activeItems.length === 0 ? (
          <div style={{ padding: 32, textAlign: 'center', color: 'var(--gray-400)', background: '#fff', borderRadius: 'var(--radius-lg)', border: '1px solid var(--gray-100)' }}>
            No active media. Upload files above and enable them.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {activeItems.map((item, i) => (
              <MediaItem key={item.id} item={item} index={i} total={activeItems.length}
                onToggle={toggle} onDelete={remove} onMove={move} />
            ))}
          </div>
        )}
      </div>

      {inactiveItems.length > 0 && (
        <div>
          <h2 style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 15, color: 'var(--gray-500)', marginBottom: 12 }}>Disabled Items</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {inactiveItems.map((item, i) => (
              <MediaItem key={item.id} item={item} index={i} total={inactiveItems.length}
                onToggle={toggle} onDelete={remove} onMove={move} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
